import { useCallback, useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { COURSES } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, FileText, Video, BookOpen, MonitorPlay, Trash2, Download, ExternalLink, Eye, X } from "lucide-react";

const ICONS = { Notes: BookOpen, Video: Video, PDF: FileText, "Recorded Session": MonitorPlay };
const COLORS = { Notes: "#0F2E4A", Video: "#DC2626", PDF: "#D97706", "Recorded Session": "#059669" };

const MIME_BY_EXT = {
  pdf: "application/pdf",
  png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", gif: "image/gif", webp: "image/webp",
  mp4: "video/mp4", webm: "video/webm", mov: "video/quicktime",
  mp3: "audio/mpeg", wav: "audio/wav",
  txt: "text/plain", csv: "text/csv",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};

function guessMime(filename = "") {
  const ext = filename.split(".").pop()?.toLowerCase();
  return MIME_BY_EXT[ext] || "application/octet-stream";
}

function b64ToBlob(b64, mime) {
  const bin = atob(b64);
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

async function toBase64(file) {
  return await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result.split(",")[1]);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function Materials() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [type, setType] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", course: COURSES[0], type: "Notes", url: "", file_name: "", file_data: "" });
  const [viewer, setViewer] = useState(null); // { blobUrl, mime, fileName, title }

  const closeViewer = () => {
    setViewer((v) => {
      if (v?.blobUrl) URL.revokeObjectURL(v.blobUrl);
      return null;
    });
  };

  const load = useCallback(async () => {
    const params = {};
    if (type !== "all") params.type = type;
    try {
      const { data } = await api.get("/materials", { params });
      setItems(data);
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Failed to load materials");
    }
  }, [type]);
  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const data = await toBase64(f);
      setForm({ ...form, file_name: f.name, file_data: data });
    } catch {
      toast.error("Failed to read file");
    }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/materials", form);
      toast.success("Material added");
      setOpen(false);
      setForm({ title: "", description: "", course: "", type: "Notes", url: "", file_name: "", file_data: "" });
      load();
    } catch (err) { toast.error(formatErr(err?.response?.data?.detail)); }
  };

  const onView = async (m) => {
    if (m.url) { window.open(m.url, "_blank", "noopener,noreferrer"); return; }
    try {
      const { data } = await api.get(`/materials/${m.id}/download`);
      if (data.url) { window.open(data.url, "_blank", "noopener,noreferrer"); return; }
      const mime = guessMime(data.file_name);
      const blob = b64ToBlob(data.file_data, mime);
      const blobUrl = URL.createObjectURL(blob);
      const previewable = mime === "application/pdf" || mime.startsWith("image/") || mime.startsWith("video/");
      if (previewable) {
        // Watermarked in-app viewer for sensitive content
        setViewer({ blobUrl, mime, fileName: data.file_name, title: m.title });
      } else {
        window.open(blobUrl, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      }
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Failed to view material");
    }
  };

  const onDownload = async (m) => {
    if (m.url) { window.open(m.url, "_blank", "noopener,noreferrer"); return; }
    try {
      const { data } = await api.get(`/materials/${m.id}/download`);
      if (data.url) { window.open(data.url, "_blank", "noopener,noreferrer"); return; }
      const mime = guessMime(data.file_name);
      const blob = b64ToBlob(data.file_data, mime);
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = data.file_name || "material";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Failed to download material");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete material?")) return;
    try {
      await api.delete(`/materials/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Failed to delete material");
    }
  };

  return (
    <div className="space-y-6" data-testid="materials-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#64748B] mb-1">Resources</div>
          <h1 className="font-heading text-3xl font-bold text-[#0F172A]">Learning Materials</h1>
        </div>
        {user.role !== "student" && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="upload-material-button" className="bg-[#0F2E4A] hover:bg-[#174169] text-white">
                <Plus className="w-4 h-4 mr-1.5" /> Upload Material
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Upload Learning Material</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-3">
                <div>
                  <Label className="text-xs font-semibold text-[#475569]">Title *</Label>
                  <Input data-testid="m-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#475569]">Course *</Label>
                  <Select value={form.course} onValueChange={(v) => setForm({ ...form, course: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="m-course"><SelectValue placeholder="Select course" /></SelectTrigger>
                    <SelectContent>
                      {COURSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#475569]">Type *</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="m-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Notes">Notes</SelectItem>
                      <SelectItem value="Video">Video</SelectItem>
                      <SelectItem value="PDF">PDF</SelectItem>
                      <SelectItem value="Recorded Session">Recorded Session</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#475569]">External URL (or upload file below)</Label>
                  <Input data-testid="m-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://..." className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#475569]">Upload File</Label>
                  <Input data-testid="m-file" type="file" onChange={onFile} className="mt-1.5" />
                  {form.file_name && <div className="text-xs text-[#64748B] mt-1">Selected: {form.file_name}</div>}
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#475569]">Description</Label>
                  <Textarea data-testid="m-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" />
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="save-material" className="bg-[#0F2E4A] hover:bg-[#174169] text-white">Save</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Tabs value={type} onValueChange={setType}>
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
          <TabsTrigger value="Notes" data-testid="tab-notes">Notes</TabsTrigger>
          <TabsTrigger value="Video" data-testid="tab-videos">Videos</TabsTrigger>
          <TabsTrigger value="PDF" data-testid="tab-pdfs">PDFs</TabsTrigger>
          <TabsTrigger value="Recorded Session" data-testid="tab-sessions">Recorded Sessions</TabsTrigger>
        </TabsList>
        <TabsContent value={type} className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
            {items.length === 0 && <div className="text-sm text-[#64748B] col-span-3 text-center py-12">No materials available.</div>}
            {items.map((m) => {
              const Icon = ICONS[m.type] || BookOpen;
              const color = COLORS[m.type] || "#0F2E4A";
              return (
                <div key={m.id} className="bg-white border border-[#E2E8F0] rounded-lg p-6 hover:shadow-md transition-all">
                  <div className="flex items-start justify-between">
                    <div className="w-10 h-10 rounded-md grid place-items-center" style={{ backgroundColor: `${color}15` }}>
                      <Icon className="w-5 h-5" style={{ color }} strokeWidth={1.75} />
                    </div>
                    <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded" style={{ backgroundColor: `${color}10`, color }}>
                      {m.type}
                    </span>
                  </div>
                  <h3 className="font-heading font-semibold text-[#0F172A] mt-4">{m.title}</h3>
                  <div className="text-xs text-[#64748B] mt-1">{m.course}</div>
                  {m.description && <p className="text-sm text-[#475569] mt-2 line-clamp-2">{m.description}</p>}
                  <div className="mt-4 flex gap-2">
                    <Button size="sm" onClick={() => onView(m)} data-testid={`view-mat-${m.id}`} className="flex-1 bg-[#0F2E4A] hover:bg-[#174169] text-white">
                      {m.url ? <><ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open</> : <><Eye className="w-3.5 h-3.5 mr-1.5" /> View</>}
                    </Button>
                    {(user.role === "admin" || user.role === "manager") && (
                      <Button size="sm" variant="outline" onClick={() => onDownload(m)} data-testid={`download-mat-${m.id}`} className="border-[#0F2E4A]/30 text-[#0F2E4A] hover:bg-[#F1F5F9]">
                        <Download className="w-3.5 h-3.5 mr-1.5" /> Download
                      </Button>
                    )}
                    {user.role !== "student" && (
                      <Button size="sm" variant="outline" onClick={() => del(m.id)} data-testid={`del-mat-${m.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {viewer && <MaterialViewer viewer={viewer} user={user} onClose={closeViewer} />}
    </div>
  );
}

/* -------------------- Watermarked in-app viewer -------------------- */

function MaterialViewer({ viewer, user, onClose }) {
  const stamp = new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour12: false });
  const identity = user.name || user.email || "user";
  const watermarkText = `${identity} • ${stamp}`;

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#0F172A]/95 flex flex-col"
      onContextMenu={(e) => e.preventDefault()}
      data-testid="material-viewer"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 bg-[#0F172A] text-white border-b border-white/10">
        <div className="min-w-0">
          <div className="font-heading font-semibold truncate">{viewer.title}</div>
          <div className="text-[11px] text-white/60 mt-0.5">
            Confidential preview — assigned to <span className="font-mono-plex text-[#34D399]">{identity}</span>
          </div>
        </div>
        <button
          onClick={onClose}
          data-testid="close-viewer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-sm font-medium transition-colors"
        >
          <X className="w-4 h-4" /> Close
        </button>
      </div>

      {/* Content + watermark */}
      <div className="relative flex-1 overflow-hidden bg-[#F1F5F9]">
        {viewer.mime === "application/pdf" && (
          <iframe
            src={`${viewer.blobUrl}#toolbar=0&navpanes=0&scrollbar=1`}
            className="absolute inset-0 w-full h-full bg-white"
            title={viewer.title}
          />
        )}
        {viewer.mime.startsWith("image/") && (
          <div className="absolute inset-0 flex items-center justify-center overflow-auto bg-[#0F172A]">
            <img src={viewer.blobUrl} alt={viewer.title} className="max-w-full max-h-full object-contain" draggable={false} />
          </div>
        )}
        {viewer.mime.startsWith("video/") && (
          <div className="absolute inset-0 flex items-center justify-center bg-black">
            <video src={viewer.blobUrl} controls controlsList="nodownload noremoteplayback" disablePictureInPicture className="max-w-full max-h-full" />
          </div>
        )}
        <WatermarkOverlay text={watermarkText} />
      </div>
    </div>
  );
}

function WatermarkOverlay({ text }) {
  // Repeating diagonal tiles of the identifier — any screenshot captures the mark
  const tiles = Array.from({ length: 40 });
  return (
    <div className="pointer-events-none absolute inset-0 select-none overflow-hidden" aria-hidden="true">
      <div className="absolute inset-[-25%] grid grid-cols-4 gap-0">
        {tiles.map((_, i) => (
          <div key={i} className="flex items-center justify-center py-16">
            <span
              className="text-[#0F2E4A] font-semibold text-sm md:text-base tracking-wider whitespace-nowrap opacity-[0.18]"
              style={{ transform: "rotate(-30deg)" }}
            >
              {text}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
